import { type Theme } from '#app/theme/index.js';
import {
  type LabelPosition,
  type TextInputSize,
  TextInputVariant,
} from './types.js';
import { css, styled } from 'styled-components';

const getHeight = ({
  variant,
}: {
  variant: TextInputVariant;
}): string | undefined => {
  if (variant === 'textArea') {
    return 'auto';
  }

  return '56px';
};

const getBackgroundColor = ({
  readOnly,
  theme,
}: {
  readOnly: boolean;
  theme: Theme;
}): string => {
  if (readOnly) {
    return theme.colorsV2.gray05;
  }

  return theme.colorsV2.white;
};

const getBorderColor = ({
  disabled,
  focused,
  hasError,
  hovered,
  readOnly,
  theme,
}: {
  disabled: boolean;
  focused: boolean;
  hasError: boolean;
  hovered: boolean;
  readOnly: boolean;
  theme: Theme;
}): string => {
  if (readOnly) {
    return theme.colorsV2.gray40;
  }

  if (disabled) {
    return theme.colorsV2.gray40;
  }

  if (hasError) {
    return theme.colorsV2.red50;
  }

  if (hovered) {
    return theme.colorsV2.gray90;
  }

  if (focused) {
    return theme.colorsV2.gray90;
  }

  return theme.colorsV2.gray40;
};

type InputWrapperProps = {
  readonly disabled: boolean;
  readonly hasError: boolean;
  readonly isInteractive: boolean;
  readonly labelPosition: LabelPosition;
  readonly largeAndCentered?: boolean;
  readonly readOnly: boolean;
  readonly size: TextInputSize;
  readonly variant: TextInputVariant;
};

export const InputWrapper = styled.div<InputWrapperProps>`
  ${({
    disabled,
    hasError,
    isInteractive,
    labelPosition,
    largeAndCentered,
    readOnly,
    size,
    theme,
    variant,
  }) => css`
    width: 100%;
    height: ${size === 'small' ? '48px' : getHeight({ variant })};
    background-color: ${getBackgroundColor({ readOnly, theme })};
    padding: ${labelPosition === 'inner' || size === 'small' ? '8px' : '14px'}
      12px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
    cursor: ${isInteractive && !disabled ? css`pointer` : undefined};
    border-radius: 8px;
    border: 1px solid
      ${getBorderColor({
        disabled,
        focused: false,
        hasError,
        hovered: false,
        readOnly,
        theme,
      })};
    transition: border-color 0.2s ease-out;

    ${largeAndCentered &&
    css`
      height: 92px;
    `}

    ${variant === 'textArea' &&
    css`
      padding: 12px;
    `}

    &:hover {
      border: 1px solid
        ${getBorderColor({
          disabled,
          focused: false,
          hasError,
          hovered: true,
          readOnly,
          theme,
        })};
    }

    &:focus-within {
      border: 1px solid
        ${getBorderColor({
          disabled,
          focused: true,
          hasError,
          hovered: false,
          readOnly,
          theme,
        })};
    }
  `}
`;
